import React, { useState } from 'react';
import { SparkleIcon, LightbulbIcon, UserFocusIcon, CircleNotchIcon } from '@phosphor-icons/react';
import { generateVoiceFromFace } from '../../lib/faceVoiceService';
import { CharacterArchetype } from '../../lib/physiognomyVoicePrompt';
import { CreditCostBadge } from '../CreditCostBadge';
import { SPECIAL_CREDIT_COSTS } from '../../lib/credits';

interface VoiceDesignInputProps {
  value: string;
  onChange: (description: string) => void;
  styleInstruction?: string;
  onStyleInstructionChange?: (instruction: string) => void;
  className?: string;
  disabled?: boolean;
  // Character image props for face-to-voice generation
  headImageUrl?: string;
  bodyImageUrl?: string;
  referenceSheetUrl?: string;
  archetype?: CharacterArchetype;
  characterName?: string;
  characterDescription?: string;
}

// Example voice descriptions for inspiration
const VOICE_EXAMPLES = [
  {
    label: 'Warm narrator',
    description: 'A warm, authoritative male voice with a deep timbre, perfect for documentary narration. Calm and reassuring with a slight resonance.',
  },
  {
    label: 'Energetic host',
    description: 'An energetic young female voice with bright, clear tones. Enthusiastic and engaging, suitable for podcasts or presentations.',
  },
  {
    label: 'Gentle storyteller',
    description: 'A gentle, soothing female voice with a soft quality. Perfect for bedtime stories or meditation, with a slight whisper quality.',
  },
  {
    label: 'Character voice',
    description: 'A gruff, weathered male voice with a slight rasp. Sounds like an experienced sea captain or wise old mentor.',
  },
  {
    label: 'Professional',
    description: 'A clear, professional female voice with perfect diction. Neutral accent, suitable for corporate presentations or tutorials.',
  },
  {
    label: 'Youthful anime',
    description: 'A bright, expressive young female voice with high energy. Characteristic of anime heroines, with dynamic emotional range.',
  },
];

export const VoiceDesignInput: React.FC<VoiceDesignInputProps> = ({
  value,
  onChange,
  styleInstruction = '',
  onStyleInstructionChange,
  className = '',
  disabled = false,
  headImageUrl,
  bodyImageUrl,
  referenceSheetUrl,
  archetype,
  characterName,
  characterDescription,
}) => {
  const [showExamples, setShowExamples] = useState(false);
  const [isGeneratingFromFace, setIsGeneratingFromFace] = useState(false);

  // Check if face-to-voice button should be shown (requires head image)
  const canGenerateFromFace = !!headImageUrl;

  const handleGenerateFromFace = async () => {
    if (!headImageUrl || isGeneratingFromFace) return;

    setIsGeneratingFromFace(true);
    try {
      const result = await generateVoiceFromFace({
        headImageUrl,
        bodyImageUrl,
        referenceSheetUrl,
        archetype,
        characterName,
        characterDescription,
      });

      if (result.success && result.voiceDescription) {
        onChange(result.voiceDescription);
      } else {
        console.error('Failed to generate voice from face:', result.error);
      }
    } catch (error) {
      console.error('Error generating voice from face:', error);
    } finally {
      setIsGeneratingFromFace(false);
    }
  };

  const handleExampleClick = (example: typeof VOICE_EXAMPLES[number]) => {
    onChange(example.description);
    setShowExamples(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;

      // Find the start of the current line
      const textBeforeCursor = value.substring(0, start);
      const lastNewline = textBeforeCursor.lastIndexOf('\n');
      const currentLineStart = lastNewline === -1 ? 0 : lastNewline + 1;
      const currentLine = value.substring(currentLineStart, start);
      const trimmedLine = currentLine.trimStart();

      // Continue bullet list if current line starts with a bullet
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
        e.preventDefault();
        const end = textarea.selectionEnd;
        const bullet = trimmedLine.charAt(0);
        const newValue = value.substring(0, start) + '\n' + bullet + ' ' + value.substring(end);
        onChange(newValue);
        // Set cursor position after the bullet point
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 3;
        }, 0);
        return;
      }

      // Continue numbered list (e.g., "1. ", "2. ")
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s/);
      if (numberedMatch) {
        e.preventDefault();
        const end = textarea.selectionEnd;
        const nextNum = parseInt(numberedMatch[1]) + 1;
        const newValue = value.substring(0, start) + '\n' + nextNum + '. ' + value.substring(end);
        onChange(newValue);
        // Set cursor position after the number
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2 + nextNum.toString().length + 1;
        }, 0);
        return;
      }
    }
  };

  // Convert markdown patterns automatically
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Check if user just typed "- " (dash followed by space)
    if (cursorPos >= 2) {
      const lastTwoChars = newValue.substring(cursorPos - 2, cursorPos);
      if (lastTwoChars === '- ') {
        // Check it's at the start of a line
        const beforeBullet = newValue.substring(0, cursorPos - 2);
        if (beforeBullet === '' || beforeBullet.endsWith('\n')) {
          newValue = newValue.substring(0, cursorPos - 2) + '• ' + newValue.substring(cursorPos);
          onChange(newValue);
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = cursorPos;
          }, 0);
          return;
        }
      }
    }

    // Convert "# " at line start to bold header style (uppercase first word)
    if (cursorPos >= 2 && newValue.substring(cursorPos - 2, cursorPos) === '# ') {
      const beforeHash = newValue.substring(0, cursorPos - 2);
      if (beforeHash === '' || beforeHash.endsWith('\n')) {
        // Keep the # as a visual header indicator in the text
        onChange(newValue);
        return;
      }
    }

    onChange(newValue);
  };

  // Convert markdown patterns for style instruction
  const handleStyleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!onStyleInstructionChange) return;

    let newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Check if user just typed "- " (dash followed by space)
    if (cursorPos >= 2) {
      const lastTwoChars = newValue.substring(cursorPos - 2, cursorPos);
      if (lastTwoChars === '- ') {
        const beforeBullet = newValue.substring(0, cursorPos - 2);
        if (beforeBullet === '' || beforeBullet.endsWith('\n')) {
          newValue = newValue.substring(0, cursorPos - 2) + '• ' + newValue.substring(cursorPos);
          onStyleInstructionChange(newValue);
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = cursorPos;
          }, 0);
          return;
        }
      }
    }
    onStyleInstructionChange(newValue);
  };

  const handleStyleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!onStyleInstructionChange) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;

      // Find the start of the current line
      const textBeforeCursor = styleInstruction.substring(0, start);
      const lastNewline = textBeforeCursor.lastIndexOf('\n');
      const currentLineStart = lastNewline === -1 ? 0 : lastNewline + 1;
      const currentLine = styleInstruction.substring(currentLineStart, start);
      const trimmedLine = currentLine.trimStart();

      // Continue bullet list if current line starts with a bullet
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
        e.preventDefault();
        const end = textarea.selectionEnd;
        const bullet = trimmedLine.charAt(0);
        const newValue = styleInstruction.substring(0, start) + '\n' + bullet + ' ' + styleInstruction.substring(end);
        onStyleInstructionChange(newValue);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 3;
        }, 0);
        return;
      }

      // Continue numbered list (e.g., "1. ", "2. ")
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s/);
      if (numberedMatch) {
        e.preventDefault();
        const end = textarea.selectionEnd;
        const nextNum = parseInt(numberedMatch[1]) + 1;
        const newValue = styleInstruction.substring(0, start) + '\n' + nextNum + '. ' + styleInstruction.substring(end);
        onStyleInstructionChange(newValue);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2 + nextNum.toString().length + 1;
        }, 0);
        return;
      }
    }
  };

  return (
    <div className={className}>
      {/* Voice Description */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs text-dust/60">
            Voice Description
            <span className="text-red-400 ml-1">*</span>
          </label>
          <div className="flex items-center gap-3">
            {canGenerateFromFace && (
              <button
                onClick={handleGenerateFromFace}
                disabled={disabled || isGeneratingFromFace}
                className="flex items-center gap-1.5 text-xs text-[#22c55e] hover:text-[#4ade80] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Analyze character's face to generate a matching voice description"
              >
                {isGeneratingFromFace ? (
                  <CircleNotchIcon className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <UserFocusIcon className="w-3.5 h-3.5" />
                )}
                <span>{isGeneratingFromFace ? 'Analyzing...' : 'Generate from face'}</span>
                <CreditCostBadge cost={SPECIAL_CREDIT_COSTS.geminiFlashText} />
              </button>
            )}
            <button
              onClick={() => setShowExamples(!showExamples)}
              className="flex items-center gap-1 text-xs text-slate-blue hover:text-slate-blue-soft transition-colors"
            >
              <LightbulbIcon className="w-3.5 h-3.5" />
              <span>{showExamples ? 'Hide' : 'Show'} examples</span>
            </button>
          </div>
        </div>

        {/* Examples dropdown */}
        {showExamples && (
          <div className="mb-3 p-3 bg-carbon border border-graphite rounded-xl">
            <p className="text-xs text-dust/40 mb-3">Click an example to use it:</p>
            <div className="grid gap-2">
              {VOICE_EXAMPLES.map((example, i) => (
                <button
                  key={i}
                  onClick={() => handleExampleClick(example)}
                  className="text-left p-2 rounded-lg hover:bg-graphite transition-colors"
                >
                  <span className="text-xs font-medium text-slate-blue">{example.label}</span>
                  <p className="text-xs text-dust/50 mt-0.5 line-clamp-2">{example.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <textarea
          value={value}
          onChange={handleDescriptionChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Describe the voice you want to create. Include details about:
• Gender and age (young woman, elderly man, etc.)
• Tone quality (warm, bright, husky, clear, etc.)
• Emotional character (calm, energetic, mysterious, etc.)
• Accent or regional flavor (optional)
• Speaking style (formal, casual, dramatic, etc.)"
          className="w-full h-40 bg-onyx border border-graphite rounded-xl p-4 text-sm text-white focus:outline-none focus:border-slate-blue/50 resize-none placeholder:text-iron disabled:opacity-50"
        />

        <div className="mt-2 flex items-start gap-2">
          <SparkleIcon className="w-4 h-4 text-slate-blue shrink-0 mt-0.5" />
          <p className="text-xs text-dust/40">
            Be specific and descriptive. Type <code className="px-1 py-0.5 bg-graphite rounded text-[10px]">-</code> for bullets, <code className="px-1 py-0.5 bg-graphite rounded text-[10px]">1.</code> for numbered lists.
          </p>
        </div>
      </div>

      {/* Vocal Direction (Optional) */}
      {onStyleInstructionChange && (
        <div>
          <label className="block text-xs text-dust/60 mb-2">
            Vocal Direction
            <span className="text-dust/30 ml-1">(Optional)</span>
          </label>
          <textarea
            value={styleInstruction}
            onChange={handleStyleChange}
            onKeyDown={handleStyleKeyDown}
            disabled={disabled}
            placeholder="Directorial notes for delivery:
• Pacing: slow and deliberate, quick and energetic
• Emotion: warm and reassuring, cold and distant
• Intensity: whispered intimacy, projected authority
• Examples: 'Speak as if comforting a friend' or 'Deliver with barely contained excitement'"
            className="w-full h-24 bg-onyx border border-graphite rounded-xl p-3 text-sm text-white focus:outline-none focus:border-slate-blue/50 resize-none placeholder:text-iron disabled:opacity-50"
          />
          <p className="mt-2 text-xs text-dust/30">
            Like a director guiding an actor—shape the emotional delivery and pacing for each performance.
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceDesignInput;
